import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { useAppliances } from "@/hooks/useAppliances";
import { useServiceLogs } from "@/hooks/useServiceLogs";
import { usePaintCodes } from "@/hooks/usePaintCodes";
import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/lib/supabase";
import { Loader2, CheckCircle, Info, Share2, FileDown, ExternalLink } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const HomeHealthReportSheet = ({ open, onOpenChange }) => {
    const { toast } = useToast();
    const { selectedProperty: activeProperty } = usePropertyContext();
    const { appliances, loading: appliancesLoading } = useAppliances(activeProperty?.id);
    const { serviceLogs, loading: logsLoading } = useServiceLogs(activeProperty?.id);
    const { paintCodes, loading: paintLoading } = usePaintCodes(activeProperty?.id);
    const [loadingScore, setLoadingScore] = useState(false);

    useEffect(() => {
        const fetchScore = async () => {
            if (!activeProperty?.id) return;
            setLoadingScore(true);
            await supabase
                .from("property_health_scores")
                .select("health_score")
                .eq("property_id", activeProperty.id)
                .maybeSingle();
            setLoadingScore(false);
        };
        if (open) fetchScore();
    }, [activeProperty?.id, open]);

    const handlePrint = () => {
        window.print();
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: `Home Health Report - ${activeProperty?.name}`,
                    text: `View the health report for ${activeProperty?.name}`,
                    url: window.location.href,
                });
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Error sharing:', error);
                }
            }
        } else {
            // Fallback: Copy to clipboard
            try {
                await navigator.clipboard.writeText(window.location.href);
                toast({
                    title: "Link Copied",
                    description: "Report link copied to clipboard",
                });
            } catch (err) {
                console.error('Failed to copy text: ', err);
            }
        }
    };

    const applianceReport = useMemo(() => {
        if (!appliances) return [];

        return appliances.map(app => {
            // Find all logs for the appliance
            const appLogs = serviceLogs
                ? serviceLogs.filter(l => l.appliance_id === app.id)
                : [];

            // Sort logs by date descending (newest first)
            const sortedLogs = appLogs.sort((a, b) => new Date(b.date) - new Date(a.date));

            // Determine status
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            let status = 'healthy';

            if (app.next_service_date) {
                const nextDate = new Date(app.next_service_date);
                nextDate.setHours(0, 0, 0, 0);

                const diffTime = nextDate.getTime() - today.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                if (diffDays < 0) {
                    status = 'overdue';
                }
            }

            return {
                ...app,
                logs: sortedLogs,
                status
            };
        });
    }, [appliances, serviceLogs]);

    const loading = appliancesLoading || logsLoading || paintLoading || loadingScore;

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <style>{printStyles}</style>
            <SheetContent side="bottom" className="h-[90vh] rounded-t-[2rem] p-0 border-t-0 bg-white sm:max-w-none no-print-overlay">
                <div className="h-full flex flex-col">
                    <SheetHeader className="px-6 pt-6 pb-4 text-left border-b border-gray-100 flex flex-row items-center justify-between">
                        <div>
                            <SheetTitle className="text-xl font-bold text-foreground flex items-center gap-2">
                                <Info className="w-5 h-5 text-primary" /> Home Health Report
                            </SheetTitle>
                            <SheetDescription>
                                Maintenance history for {activeProperty?.name}
                            </SheetDescription>
                        </div>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" className="gap-2 rounded-xl bg-white shadow-sm border-gray-100 hover:bg-gray-50 no-print">
                                    <Share2 className="w-4 h-4 text-primary" />
                                    <span className="hidden xs:inline">Export Options</span>
                                    <span className="inline xs:hidden">Export</span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48 rounded-xl p-1 shadow-lg border-gray-100 z-[100]">
                                <DropdownMenuItem
                                    onClick={handleShare}
                                    className="flex items-center gap-2 py-2.5 rounded-lg cursor-pointer focus:bg-primary/5 focus:text-primary transition-colors"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                    <span className="font-medium">Share Link</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={handlePrint}
                                    className="flex items-center gap-2 py-2.5 rounded-lg cursor-pointer focus:bg-emerald-50 focus:text-emerald-600 transition-colors"
                                >
                                    <FileDown className="w-4 h-4" />
                                    <span className="font-medium">Download PDF</span>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </SheetHeader>

                    {loading ? (
                        <div className="flex-1 flex items-center justify-center">
                            <Loader2 className="w-8 h-8 text-primary animate-spin" />
                        </div>
                    ) : (
                        <ScrollArea id="print-report" className="flex-1 px-8 py-6 bg-white print:p-0">
                            {/* Print-specific header (hidden on screen usually, but visible here for preview) */}
                            <div className="mb-8 hidden print:block">
                                <h1 className="text-2xl font-bold text-gray-900">{activeProperty?.name || "Property Report"}</h1>
                                <p className="text-gray-500">{activeProperty?.address}</p>
                                <p className="text-sm text-gray-400 mt-1">Generated on {new Date().toLocaleDateString()}</p>
                            </div>

                            <div className="space-y-8 pb-20 print:pb-0">
                                {applianceReport.length === 0 ? (
                                    <div className="text-center py-8 text-muted-foreground">
                                        No appliances tracked.
                                    </div>
                                ) : (
                                    applianceReport.map((item) => (
                                        <div key={item.id} className="break-inside-avoid">
                                            {/* Appliance Header */}
                                            <div className="flex justify-between items-end mb-2">
                                                <div>
                                                    <h3 className="text-lg font-bold text-gray-900">{item.name}</h3>
                                                    <p className="text-sm font-medium text-gray-500">{item.brand || "Unknown Brand"}</p>
                                                </div>
                                                <div className="text-right">
                                                    {item.installation_date && (
                                                        <p className="text-xs text-gray-400">
                                                            Installed: {new Date(item.installation_date).toLocaleDateString()}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Divider */}
                                            <hr className="border-gray-200 mb-4" />

                                            {/* Service History */}
                                            <div className="space-y-3 pl-2">
                                                {item.logs.length === 0 ? (
                                                    <p className="text-sm text-gray-400 italic">No service history recorded.</p>
                                                ) : (
                                                    <>
                                                        {/* Desktop/Tablet Table View */}
                                                        <div className="hidden sm:block overflow-x-auto">
                                                            <table className="w-full text-sm text-left">
                                                                <thead className="text-xs text-gray-500 uppercase bg-gray-50">
                                                                    <tr>
                                                                        <th className="px-3 py-2 rounded-l-md">Date</th>
                                                                        <th className="px-3 py-2">Provider</th>
                                                                        <th className="px-3 py-2">Details</th>
                                                                        <th className="px-3 py-2 text-right rounded-r-md">Cost</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y divide-gray-100">
                                                                    {item.logs.map((log) => (
                                                                        <tr key={log.id}>
                                                                            <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                                                                                {new Date(log.date).toLocaleDateString()}
                                                                            </td>
                                                                            <td className="px-3 py-2 text-gray-600">
                                                                                {log.technician_name || log.provider_name || "Unknown"}
                                                                            </td>
                                                                            <td className="px-3 py-2 text-gray-600 max-w-[200px] truncate">
                                                                                {log.description || "-"}
                                                                            </td>
                                                                            <td className="px-3 py-2 text-right font-medium text-gray-900">
                                                                                {log.cost ? `R${log.cost.toLocaleString()}` : "-"}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>

                                                        {/* Mobile Card View */}
                                                        <div className="sm:hidden space-y-3">
                                                            {item.logs.map((log) => (
                                                                <div key={log.id} className="bg-gray-50/50 rounded-lg p-3 border border-gray-100">
                                                                    <div className="flex justify-between items-start mb-1">
                                                                        <span className="font-medium text-sm text-gray-900">
                                                                            {new Date(log.date).toLocaleDateString()}
                                                                        </span>
                                                                        <span className="font-medium text-sm text-gray-900">
                                                                            {log.cost ? `R${log.cost.toLocaleString()}` : "-"}
                                                                        </span>
                                                                    </div>
                                                                    <div className="text-xs text-gray-500 mb-1">
                                                                        {log.technician_name || log.provider_name || "Unknown Provider"}
                                                                    </div>
                                                                    <p className="text-sm text-gray-600 leading-snug">
                                                                        {log.description || "No details provided"}
                                                                    </p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}

                                {/* Paint Section */}
                                <div className="space-y-6 pt-8 break-before-page">
                                    <div className="flex items-center justify-between border-b-2 border-primary/20 pb-2">
                                        <h2 className="text-xl font-bold text-foreground tracking-tight">Paint Specifications</h2>
                                        <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                                            Interior & Exterior
                                        </span>
                                    </div>

                                    {paintCodes.length === 0 ? (
                                        <p className="text-sm text-gray-400 italic py-4">No paint codes recorded.</p>
                                    ) : (
                                        <div className="space-y-4">
                                            {paintCodes.map((paint) => (
                                                <div key={paint.id} className="bg-gray-50/50 rounded-xl p-4 border border-gray-100 break-inside-avoid">
                                                    <div className="flex justify-between items-start mb-3">
                                                        <h4 className="font-bold text-gray-900">{paint.room}</h4>
                                                        {paint.last_painted && (
                                                            <span className="text-[10px] text-gray-400 font-medium whitespace-nowrap">
                                                                Last: {new Date(paint.last_painted).toLocaleDateString()}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="space-y-2">
                                                        <div className="flex justify-between text-xs">
                                                            <span className="text-gray-500">Brand</span>
                                                            <span className="font-semibold text-gray-700">{paint.brand}</span>
                                                        </div>
                                                        <div className="flex justify-between text-xs">
                                                            <span className="text-gray-500">Color Code</span>
                                                            <span className="font-semibold text-primary">{paint.code}</span>
                                                        </div>
                                                        {paint.finish && (
                                                            <div className="flex justify-between text-xs">
                                                                <span className="text-gray-500">Finish</span>
                                                                <span className="font-semibold text-gray-700">{paint.finish}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </ScrollArea>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};

const printStyles = `
@media print {
    body * {
        visibility: hidden;
    }
    #print-report, #print-report * {
        visibility: visible;
    }
    #print-report {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        padding: 0;
        margin: 0;
        background: white;
    }
    .sheet-header, .sheet-footer, [data-radix-collection-item] {
        display: none !important;
    }
    .no-print {
        display: none !important;
    }
}
`;

export default HomeHealthReportSheet;
